import React, { useEffect, useState, createElement } from 'react';
import { Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { Hero } from './components/Hero';
import { MarketingHero } from './components/MarketingHero';
import { ProductDemo } from './components/ProductDemo';
import { AICoworkers } from './components/AICoworkers';
import { StayInControl } from './components/StayInControl';
import { ToolComparison } from './components/ToolComparison';
import { HowItWorks } from './components/HowItWorks';
import { HowItWorksConnections } from './components/HowItWorksConnections';
import { AlwaysOn } from './components/AlwaysOn';
import { CapabilityGrid } from './components/CapabilityGrid';
import { QuickCapture } from './components/QuickCapture';
import { LivesInSlack } from './components/LivesInSlack';
import { ConnectedWorkflow } from './components/ConnectedWorkflow';
import { ComputerUse } from './components/ComputerUse';
import { Pricing } from './pages/Pricing';
import { WallOfLove } from './components/WallOfLove';
import { NarrativeSection } from './components/NarrativeSection';
import { MemorySection } from './components/MemorySection';
import { SecuritySection } from './components/SecuritySection';
import { PricingSection } from './components/PricingSection';
import { ClosingCTA } from './components/ClosingCTA';
import { Layout } from './components/Layout';
import { IntroSplash } from './components/IntroSplash';
import { Terms } from './pages/Terms';
import { AffiliateTerms } from './pages/AffiliateTerms';
import { Privacy } from './pages/Privacy';
import { VulnerabilityDisclosure } from './pages/VulnerabilityDisclosure';
import { Contact } from './pages/Contact';
import { Support } from './pages/Support';
import { Careers } from './pages/Careers';
import { Enterprise } from './pages/Enterprise';
import { EnterprisePlatform } from './pages/EnterprisePlatform';
import { ClientManagement } from './pages/ClientManagement';
import { GrowthMarketing } from './pages/GrowthMarketing';
import { FoundersExecs } from './pages/FoundersExecs';
import { Signup } from './pages/Signup';
import { TrialFlow } from './pages/TrialFlow';
import { Blog } from './pages/Blog';
import { Research } from './pages/Research';
import { CaseStudies } from './pages/CaseStudies';
import { Changelog } from './pages/Changelog';
import { Security } from './pages/Security';
import { Integrations } from './pages/Integrations';
import { IntegrationDetail } from './pages/IntegrationDetail';
import { Bespoke } from './pages/Bespoke';
import { Consulting } from './pages/Consulting';
import { CompareChatGPT } from './pages/CompareChatGPT';
import { GigaForChatGPT } from './pages/GigaForChatGPT';
import { GigaForChatGPT2 } from './pages/GigaForChatGPT2';
import { CompanyBrain } from './pages/CompanyBrain';
import { WatchGigaDemo } from './pages/WatchGigaDemo';
import { BookDemo } from './pages/BookDemo';
import { Agents } from './pages/Agents';
import { Home } from './pages/Home';
import { HomeAlternative } from './pages/HomeAlternative';
import { HomeArchitecture } from './pages/HomeArchitecture';
import { EcommerceHome } from './pages/EcommerceHome';
import { FutureHome } from './pages/FutureHome';
import { WorkspaceHome } from './pages/WorkspaceHome';
import { HomeTeamWorkspace } from './pages/HomeTeamWorkspace';
import { ReferralCapture } from './pages/ReferralCapture';
import { Dashboard } from './pages/Dashboard';
import { WorkflowShow } from './pages/WorkflowShow';
import { OnboardingFunnel } from './pages/OnboardingFunnel';
import { WorkspaceAlreadyConnected } from './pages/WorkspaceAlreadyConnected';
import { SphereDirections } from './pages/SphereDirections';
import { ChatTransition } from './pages/ChatTransition';
import { UseCasesConcepts } from './pages/UseCasesConcepts';
import { FontOptions } from './pages/FontOptions';
import { ClaudeFont } from './pages/ClaudeFont';
import { CreatorProgram } from './pages/CreatorProgram';
import { ViktorAlternative } from './pages/ViktorAlternative';
import { FeatureDetail } from './pages/FeatureDetail';
import { SignIn } from './pages/SignIn';
import { Developers } from './pages/Developers';
import { NewInfrastructure } from './pages/NewInfrastructure';
import { Alt } from './pages/Alt';
import { CopyModeProvider } from './components/copy/copyMode';
import { resolveRouteMeta, isNoindexRoute } from './lib/routeMeta';
/**
 * Scrolls the window to the top on every route change.
 */
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
/**
 * Sets <title> and <meta name="description"> per route.
 * Gets baked into each static HTML file by the prerenderer.
 */
function PageMeta() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    // Titles/descriptions live in src/lib/routeMeta.ts so the build-time
    // prerender can bake the SAME values into each static HTML file.
    const {
      title,
      desc
    } = resolveRouteMeta(pathname);
    document.title = title;
    let canonical = document.querySelector('link[rel="canonical"]');
    const canonicalHref = `https://getgiga.com${pathname === '/' ? '/' : pathname}`;
    if (!isNoindexRoute(pathname)) {
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', canonicalHref);
    } else if (canonical) {
      canonical.remove();
    }
    const meta = document.querySelector('meta[name="description"]');
    if (desc) {
      if (meta) {
        meta.setAttribute('content', desc);
      } else {
        const el = document.createElement('meta');
        el.name = 'description';
        el.content = desc;
        document.head.appendChild(el);
      }
    }
    // Keep search engines out of the /unlisted and /app-screen-mockups
    // namespaces. This mirrors the noindex meta baked into the prerendered
    // HTML (see prerender.mjs) so it's correct on client-side navigation too.
    let robots = document.querySelector('meta[name="robots"]');
    if (isNoindexRoute(pathname)) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex, nofollow');
    } else if (robots) {
      // Returning to a public route — remove the restrictive directive.
      robots.remove();
    }
  }, [pathname]);
  return null;
}
export function App() {
  return <CopyModeProvider data-id="element-0" data-mp="2ti8i53m01fod">
      <ScrollToTop data-id="element-1" data-mp="2tkkvf3gtb1v3" />
      <PageMeta data-id="element-2" data-mp="2phesa4ozxqsu" />
      {/* Global Prado-style film grain over the entire app */}
      <div className="grain-global" aria-hidden="true" data-id="element-3" data-mp="2pjivslizgnsl" />
      <Routes data-id="element-4" data-mp="33hplheds3hh6">
        {/* ──────────────────────────────────────────────────────────────
         APP SCREEN MOCKUPS — product/app UI mockups the marketing team uses
         for reviews. These are NOT the live product (the real signup/login
         lives on app.getgiga.com). Grouped under /app-screen-mockups and
         marked noindex (see routeMeta.isNoindexRoute) so search engines skip
         them. Not linked from the site nav.
         ────────────────────────────────────────────────────────────── */}
        <Route path="/app-screen-mockups/signup" element={<Signup data-id="element-6" data-mp="2zclovcxnztbm" />} data-id="element-5" data-mp="3d2p4vm5yaqsm" />
        {/* Direct routes onto individual signup screens, so they can be opened
         from the route dropdown without clicking through the flow. */}
        <Route path="/app-screen-mockups/google-consent" element={<Signup data-id="element-8" data-mp="2pga7w9n4qfau" />} data-id="element-7" data-mp="345on3yddzqy5" />
        <Route path="/app-screen-mockups/google-permissions" element={<Signup data-id="element-10" data-mp="2uf3owc1mkofr" />} data-id="element-9" data-mp="2pgsbxsg19xtb" />
        <Route path="/app-screen-mockups/giga-chat" element={<Signup data-id="element-12" data-mp="2gej7nkbcz0od" />} data-id="element-11" data-mp="2uek4mooi5nyk" />
        <Route path="/app-screen-mockups/sign-in" element={<SignIn data-id="element-14" data-mp="2ggn6voybbbib" />} data-id="element-13" data-mp="2ge12tzamre4u" />
        <Route path="/app-screen-mockups/trial" element={<TrialFlow data-id="element-16" data-mp="2kfcu0995k7f5" />} data-id="element-15" data-mp="2kfmly2drconh" />
        <Route path="/app-screen-mockups/dashboard" element={<Dashboard data-id="element-18" data-mp="30uc7u1qoo6la" />} data-id="element-17" data-mp="2khz34dboyuzh" />
        <Route path="/app-screen-mockups/workflow-show" element={<WorkflowShow data-id="element-20" data-mp="2vvr8e4j99qxw" />} data-id="element-19" data-mp="30uvgssycvkn6" />
        <Route path="/app-screen-mockups/onboarding-funnel" element={<OnboardingFunnel data-id="element-22" data-mp="2qwxtc7oblrvm" />} data-id="element-21" data-mp="2vxltpjyfvl2i" />
        <Route path="/app-screen-mockups/slack-oauth-callback" element={<WorkspaceAlreadyConnected data-id="element-24" data-mp="2lyd2gyuykkju" />} data-id="element-23" data-mp="2qw69fu435il6" />

        {/* Waitlist referral capture stays PUBLIC + functional: /r/<code>
         records the inbound referral and forwards to app.getgiga.com. */}
        <Route path="/r/:code" element={<ReferralCapture data-id="element-26" data-mp="2d5j61ondr4ir" />} data-id="element-25" data-mp="2d5t1hrr723vu" />

        {/* ──────────────────────────────────────────────────────────────
         UNLISTED (standalone, no marketing chrome) — internal review /
         experimental screens. Grouped under /unlisted and marked noindex.
         ────────────────────────────────────────────────────────────── */}
        <Route path="/unlisted/sphere-directions" element={<SphereDirections data-id="element-28" data-mp="1u76uxxcwrkkj" />} data-id="element-27" data-mp="1y80nx557jpka" />
        <Route path="/unlisted/chat-transition" element={<ChatTransition data-id="element-30" data-mp="9owfofujaeo4" />} data-id="element-29" data-mp="1u889ibt4up70" />
        <Route path="/unlisted/use-cases-concepts" element={<UseCasesConcepts data-id="element-32" data-mp="9qz4iwjya1r9" />} data-id="element-31" data-mp="9pnzkterqnyk" />
        <Route path="/unlisted/font-options" element={<FontOptions data-id="element-34" data-mp="dows5uy2ofsj" />} data-id="element-33" data-mp="dq7x3y397tl8" />
        <Route path="/unlisted/claude-font" element={<ClaudeFont data-id="element-36" data-mp="ipb43o2so9vu" />} data-id="element-35" data-mp="dscad6n1m4y6" />

        {/* ──────────────────────────────────────────────────────────────
         UNLISTED (rendered inside the marketing Layout) — home variants,
         unlinked offering pages, and pages parked for later. Same /unlisted
         prefix + noindex, but keep the header/footer chrome so they still
         look like real pages during review.
         ────────────────────────────────────────────────────────────── */}
        <Route path="/unlisted" element={<Layout data-id="element-38" data-mp="4pjkn7tie1ag" />} data-id="element-37" data-mp="3w2e7bat4rix1">
          {/* Home variants / previews */}
          <Route path="architecture" element={<HomeArchitecture data-id="element-40" data-mp="33gbjzt5k6pby" />} data-id="element-39" data-mp="4rwas89f7hyl" />
          <Route path="home-alt" element={<HomeAlternative data-id="element-42" data-mp="33dpctumkybu9" />} data-id="element-41" data-mp="33f0hrxrrhpmy" />
          <Route path="ecommerce" element={<EcommerceHome data-id="element-44" data-mp="38dlgfsrd819a" />} data-id="element-43" data-mp="38ed1war90ilu" />
          <Route path="future" element={<FutureHome data-id="element-46" data-mp="2f7djskc0kwln" />} data-id="element-45" data-mp="38ctkggtuw1ih" />
          <Route path="agents" element={<Agents data-id="element-48" data-mp="2f6vi386l1q6c" />} data-id="element-47" data-mp="2f63m3w92pqfj" />
          <Route path="workspace" element={<WorkspaceHome data-id="element-50" data-mp="2k5f5ld0tra0t" />} data-id="element-49" data-mp="2k682lm9zel5r" />
          <Route path="team-workspace" element={<HomeTeamWorkspace data-id="element-52" data-mp="2p5b8ta1p6xfb" />} data-id="element-51" data-mp="2k4n8tyvhr68y" />
          {/* Offering pages, currently unlinked */}
          <Route path="enterprise" element={<Enterprise data-id="element-54" data-mp="2u4xlnidm6bnl" />} data-id="element-53" data-mp="2p3gnhumil3ap" />
          <Route path="enterprise-platform" element={<EnterprisePlatform data-id="element-56" data-mp="2u21kzmadt8vg" />} data-id="element-55" data-mp="2u331w7e2wpl3" />
          <Route path="bespoke" element={<Bespoke data-id="element-58" data-mp="1wi70av3ec0ys" />} data-id="element-57" data-mp="1whf3xi1z5z7g" />
          <Route path="consulting" element={<Consulting data-id="element-60" data-mp="21f76r745r5qf" />} data-id="element-59" data-mp="1wesv7f3cldnn" />
          <Route path="security" element={<Security data-id="element-62" data-mp="21dmczjpo3qtm" />} data-id="element-61" data-mp="21gibb95fghil" />
          {/* Content, parked until ready */}
          <Route path="blog" element={<Blog data-id="element-64" data-mp="26cqm6acldojy" />} data-id="element-63" data-mp="26d8o9nlxqwzs" />
          <Route path="research" element={<Research data-id="element-66" data-mp="2b6laxs720tpw" />} data-id="element-65" data-mp="2b59wmyfzg3kj" />
          <Route path="case-studies" element={<CaseStudies data-id="element-68" data-mp="1dq5rp10tkwc6" />} data-id="element-67" data-mp="2b6ur6o2armie" />
          <Route path="changelog" element={<Changelog data-id="element-70" data-mp="1dpmj4ax27kat" />} data-id="element-69" data-mp="1dqxogf65l041" />
          {/* Use cases, parked until ready */}
          <Route path="use-cases/founders-execs" element={<FoundersExecs data-id="element-72" data-mp="1imw6zm4svnwj" />} data-id="element-71" data-mp="1io7bxp9zf1p8" />
          <Route path="use-cases/client-management" element={<ClientManagement data-id="element-74" data-mp="3vyph3ezgg0d4" />} data-id="element-73" data-mp="3w00kveswh848" />
          <Route path="use-cases/growth-marketing" element={<GrowthMarketing data-id="element-76" data-mp="3ryf2jdxlk1mn" />} data-id="element-75" data-mp="3ry6o3hltso9c" />
          {/* Comparison pages, parked until ready */}
          <Route path="compare/chatgpt" element={<CompareChatGPT data-id="element-78" data-mp="3n0wzg469ggmk" />} data-id="element-77" data-mp="3mxizf2l2kjia" />
        </Route>

        {/* ──────────────────────────────────────────────────────────────
         PUBLIC marketing site — indexable.
         ────────────────────────────────────────────────────────────── */}
        <Route path="/" element={<Layout data-id="element-80" data-mp="3894py8t5dwsr" />} data-id="element-79" data-mp="389o4rgni452k">
          {/* New home screen, built on the Tasklet-style hero. */}
          <Route index element={<Home data-id="element-82" data-mp="348kquzp0kj3z" />} data-id="element-81" data-mp="349dxm0nz38m4" />
          <Route path="terms" element={<Terms data-id="element-84" data-mp="2z9rni03fy8hj" />} data-id="element-83" data-mp="345yi4wqdzxk6" />
          <Route path="affiliate-terms-reditus" element={<AffiliateTerms data-id="element-86" data-mp="2z7exr0rfyttx" />} data-id="element-85" data-mp="2zaau4knn5ag9" />
          <Route path="privacy" element={<Privacy data-id="element-88" data-mp="2u8txx2g3qc60" />} data-id="element-87" data-mp="2uaog48zznq6e" />
          <Route path="vulnerability-disclosure" element={<VulnerabilityDisclosure data-id="element-90" data-mp="2pasbq8ndpwqb" />} data-id="element-89" data-mp="2uc9anymaz949" />
          <Route path="contact" element={<Contact data-id="element-92" data-mp="2fng2l1j7j8r9" />} data-id="element-91" data-mp="2pbtz9cll0box" />
          <Route path="support" element={<Support data-id="element-94" data-mp="2flv6h7h8vhra" />} data-id="element-93" data-mp="2fn6l62c8a9x6" />
          <Route path="developers" element={<Developers data-id="element-96" data-mp="2kn2en7nauiyh" />} data-id="element-95" data-mp="2fpixnzz7vs2u" />
          <Route path="developers/api" element={<Developers data-id="element-98" data-mp="2kkg9tfrsmhjy" />} data-id="element-97" data-mp="2knc5syk2yw5r" />
          <Route path="developers/openapi" element={<Developers data-id="element-100" data-mp="26ocgrpsnof92" />} data-id="element-99" data-mp="26n1ghzyf5pmp" />
          <Route path="developers/authentication" element={<Developers data-id="element-102" data-mp="2anlcv1b64p7s" />} data-id="element-101" data-mp="26ouj945wvppf" />
          <Route path="developers/webhooks" element={<Developers data-id="element-104" data-mp="2ye7i6ktsfve3" />} data-id="element-103" data-mp="2aov8zkygnnbs" />
          <Route path="developers/mcp" element={<Developers data-id="element-106" data-mp="2yepknz71n5ug" />} data-id="element-105" data-mp="2ydo1t8jsdf26" />
          <Route path="new" element={<NewInfrastructure data-id="element-108" data-mp="33dabx94bif6r" />} data-id="element-107" data-mp="33d0uwb193id7" />
          <Route path="alt" element={<Alt data-id="element-110" data-mp="33ev6gyqmty4m" />} data-id="element-109" data-mp="33cioilkvjnrk" />
          {/* Enterprise positioning variant. Lives at a top-level path so it is
           easy to open and preview directly; kept out of the index via
           NOINDEX_ROUTES. */}
          <Route path="enterprise-platform" element={<EnterprisePlatform data-id="element-112" data-mp="3hbqt5tzt3o35" />} data-id="element-111" data-mp="3hctj9zpb1mi0" />
          <Route path="careers" element={<Careers data-id="element-114" data-mp="3m39y9o8rxg6h" />} data-id="element-113" data-mp="3hap58oxoz740" />
        <Route path="pricing" element={<Pricing data-id="element-116" data-mp="3m538temickjr" />} data-id="element-115" data-mp="3m3s0d1i4aomb" />
          <Route path="giga-for-chatgpt" element={<GigaForChatGPT data-id="element-118" data-mp="1anri54emeizk" />} data-id="element-117" data-mp="3m5cyt27jyrpg" />
          <Route path="chatgpt" element={<GigaForChatGPT2 data-id="element-120" data-mp="1am6rvqzc9m7i" />} data-id="element-119" data-mp="1amylwxdafbvo" />
          <Route path="company-brain" element={<CompanyBrain data-id="element-122" data-mp="15ox0i5se30h1" />} data-id="element-121" data-mp="15q87eeh4socd" />
          <Route path="giga-for-chatgpt-2" element={<Navigate to="/chatgpt" replace data-id="element-124" data-mp="1kdb2kehzekwj" />} data-id="element-123" data-mp="1kecrnmvu17x9" />
          <Route path="creator-program" element={<CreatorProgram data-id="element-126" data-mp="1fezti5zigefd" />} data-id="element-125" data-mp="1kg72279h9hn3" />
          <Route path="viktor-alternative" element={<ViktorAlternative data-id="element-128" data-mp="rpp23f41vwdw" />} data-id="element-127" data-mp="1ffrrfngkyo8t" />
          <Route path="integrations" element={<Integrations data-id="element-130" data-mp="rpxe74scmxo1" />} data-id="element-129" data-mp="rqqcdhd8seuk" />
          {/* Data-driven per-integration pages, sourced from the Giga catalog
            (single source of truth). Replaces the old one-off pages. */}
          <Route path="integrations/:slug" element={<IntegrationDetail data-id="element-132" data-mp="woruo02uga4z" />} data-id="element-131" data-mp="11pfw7fop89dg" />
          <Route path="features/:slug" element={<FeatureDetail data-id="element-134" data-mp="wngs23l4x8fg" />} data-id="element-133" data-mp="woicgyo1j79u" />
          <Route path="features/connect-to-slack" element={<Navigate to="/features/giga-in-slack" replace data-id="element-136" data-mp="2d1p5n8nxg7ey" />} data-id="element-135" data-mp="2d308n69jtb50" />
          <Route path="features/integrate-with-databases" element={<Navigate to="/features/connect-to-databases" replace data-id="element-138" data-mp="283ngq73tldvk" />} data-id="element-137" data-mp="284ghmrg3l95s" />
          <Route path="features/backed-by-a-full-computer" element={<Navigate to="/features/giga-cloud-computer" replace data-id="element-140" data-mp="mlwhusmgl0gz" />} data-id="element-139" data-mp="ho4u8asuk0il" />
          <Route path="features/giga-cloud-computer" element={<Navigate to="/features/a-full-computer" replace data-id="element-142" data-mp="qn8e3gzu8jy5" />} data-id="element-141" data-mp="mm69ekn5jfos" />
          <Route path="features/browser-control" element={<Navigate to="/features/cloud-browser" replace data-id="element-144" data-mp="vnnnxykwv9bd" />} data-id="element-143" data-mp="qpuk3c72yre9" />
          <Route path="demo" element={<WatchGigaDemo data-id="element-146" data-mp="10mh3rxno7cep" />} data-id="element-145" data-mp="10lpawulgjss4" />
          <Route path="demo/book" element={<BookDemo data-id="element-148" data-mp="15kse09hvnoxg" />} data-id="element-147" data-mp="10l5y1sanzuky" />
        </Route>
      </Routes>
    </CopyModeProvider>;
}